import type { ContentRef } from '@cogo/publisher-core'
import { hashCanonicalContent } from '@cogo/schema/canonical-content-hash'

import type { GeneratedApplicationAuthoritySubject } from '@/features/generated-application-authority'
import {
  cogoPlatformApiUrl,
  withCogoDevProxyHeaderRecord,
} from '@/services/cogo-platform-config'

export const CHAT_GENERATED_APPLICATION_DRAFT_PREPARATION_SCHEMA_VERSION =
  'cogo.platform-chat-generated-application-draft-preparation.v2' as const
export const CHAT_GENERATED_APPLICATION_DRAFT_FINALIZATION_SCHEMA_VERSION =
  'cogo.platform-chat-generated-application-draft-finalization.v1' as const
export const CHAT_GENERATED_APPLICATION_DRAFT_PREPARATION_ROUTE =
  '/api/chat/generated-application-drafts' as const

const CONTENT_HASH = /^sha256:[a-f0-9]{64}$/
const SAFE_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,255}$/
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

type JsonRecord = Record<string, unknown>

export interface ChatGeneratedApplicationDraftPreparationReceipt {
  schemaVersion: typeof CHAT_GENERATED_APPLICATION_DRAFT_PREPARATION_SCHEMA_VERSION
  status: 'prepared'
  idempotencyKey: string
  requestContentHash: string
  subject: GeneratedApplicationAuthoritySubject
  projectRevision: {
    authority: 'studio-project-server'
    value: string
  }
  settingsRevision: {
    authority: 'studio-project-settings-server'
    value: string
  }
  requestEnvelopeRef: ContentRef
  preparedAt: string
  receiptRef: ContentRef
}

export class ChatGeneratedApplicationDraftPreparationError extends Error {
  override readonly name = 'ChatGeneratedApplicationDraftPreparationError'

  constructor(
    readonly status: number,
    readonly code: string,
    readonly retryable: boolean | null,
  ) {
    super(`${code}${status > 0 ? ` (HTTP ${status})` : ''}`)
  }
}

function record(value: unknown): JsonRecord | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as JsonRecord)
    : null
}

function exactKeys(value: unknown, expected: readonly string[]): boolean {
  const candidate = record(value)
  if (!candidate) return false
  const observed = Object.keys(candidate).sort()
  const canonical = [...expected].sort()
  return (
    observed.length === canonical.length &&
    observed.every((key, index) => key === canonical[index])
  )
}

function contentRef(value: unknown): value is ContentRef {
  const candidate = record(value)
  return Boolean(
    candidate &&
      exactKeys(candidate, ['ref', 'contentHash']) &&
      typeof candidate.ref === 'string' &&
      candidate.ref.length > 0 &&
      typeof candidate.contentHash === 'string' &&
      CONTENT_HASH.test(candidate.contentHash),
  )
}

export function parseChatGeneratedApplicationDraftPreparationReceipt(
  value: unknown,
  expected?: { idempotencyKey: string; requestContent: string },
): ChatGeneratedApplicationDraftPreparationReceipt {
  if (
    !exactKeys(value, [
      'schemaVersion',
      'status',
      'idempotencyKey',
      'requestContentHash',
      'subject',
      'projectRevision',
      'settingsRevision',
      'requestEnvelopeRef',
      'preparedAt',
      'receiptRef',
    ])
  ) {
    throw new Error('chat-draft-preparation-response-invalid')
  }
  const receipt = value as unknown as ChatGeneratedApplicationDraftPreparationReceipt
  const subject = record(receipt.subject)
  const expectedContentHash = expected
    ? hashCanonicalContent({ requestContent: expected.requestContent })
    : null
  if (
    receipt.schemaVersion !==
      CHAT_GENERATED_APPLICATION_DRAFT_PREPARATION_SCHEMA_VERSION ||
    receipt.status !== 'prepared' ||
    !SAFE_ID.test(receipt.idempotencyKey) ||
    (expected && receipt.idempotencyKey !== expected.idempotencyKey) ||
    !CONTENT_HASH.test(receipt.requestContentHash) ||
    (expectedContentHash !== null &&
      receipt.requestContentHash !== expectedContentHash) ||
    !subject ||
    !exactKeys(subject, [
      'schemaVersion',
      'tenantId',
      'actorId',
      'projectId',
      'applicationInstanceId',
      'generationRequestId',
    ]) ||
    subject.schemaVersion !==
      'cogo.studio-generated-application-authority-subject.v1' ||
    typeof subject.tenantId !== 'string' ||
    !UUID.test(subject.tenantId) ||
    typeof subject.actorId !== 'string' ||
    !UUID.test(subject.actorId) ||
    typeof subject.projectId !== 'string' ||
    !UUID.test(subject.projectId) ||
    subject.applicationInstanceId !== subject.projectId ||
    typeof subject.generationRequestId !== 'string' ||
    !UUID.test(subject.generationRequestId) ||
    !exactKeys(receipt.projectRevision, ['authority', 'value']) ||
    receipt.projectRevision.authority !== 'studio-project-server' ||
    receipt.projectRevision.value !== `${subject.projectId}@1` ||
    !exactKeys(receipt.settingsRevision, ['authority', 'value']) ||
    receipt.settingsRevision.authority !==
      'studio-project-settings-server' ||
    receipt.settingsRevision.value !== `${subject.projectId}@1` ||
    !contentRef(receipt.requestEnvelopeRef) ||
    receipt.requestEnvelopeRef.ref !==
      `cogo-content:chat-generated-application-draft-request:${encodeURIComponent(receipt.idempotencyKey)}` ||
    typeof receipt.preparedAt !== 'string' ||
    Number.isNaN(Date.parse(receipt.preparedAt)) ||
    !contentRef(receipt.receiptRef) ||
    receipt.receiptRef.ref !==
      `cogo-content:chat-generated-application-draft-preparation:${encodeURIComponent(receipt.idempotencyKey)}`
  ) {
    throw new Error('chat-draft-preparation-receipt-invalid')
  }
  const { receiptRef, ...receiptBody } = receipt
  if (receiptRef.contentHash !== hashCanonicalContent(receiptBody)) {
    throw new Error('chat-draft-preparation-receipt-self-hash-invalid')
  }
  return structuredClone(receipt)
}

function responseError(status: number, payload: unknown) {
  const body = record(payload)
  return new ChatGeneratedApplicationDraftPreparationError(
    status,
    typeof body?.code === 'string'
      ? body.code
      : typeof body?.error === 'string'
        ? body.error
        : `chat-generated-application-draft-http-${status}`,
    typeof body?.retryable === 'boolean'
      ? body.retryable
      : status === 409
        ? false
        : [429, 502, 503, 504].includes(status)
          ? true
          : null,
  )
}

async function sendPreparationRequest(input: {
  accessToken: string
  idempotencyKey: string
  requestContent: string
  signal?: AbortSignal
}): Promise<Response> {
  return fetch(cogoPlatformApiUrl(CHAT_GENERATED_APPLICATION_DRAFT_PREPARATION_ROUTE), {
    method: 'POST',
    headers: withCogoDevProxyHeaderRecord({
      Authorization: `Bearer ${input.accessToken}`,
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify({
      idempotencyKey: input.idempotencyKey,
      requestContent: input.requestContent,
    }),
    signal: input.signal,
  })
}

/**
 * Reserve the authenticated Chat generation subject before decomposition.
 * One transport retry deliberately reuses the same opaque key: a lost 201
 * response reads back the server's exact project and receipt instead of
 * creating a second draft.
 */
export async function prepareChatGeneratedApplicationDraft(input: {
  accessToken: string
  idempotencyKey: string
  requestContent: string
  signal?: AbortSignal
}): Promise<ChatGeneratedApplicationDraftPreparationReceipt> {
  const accessToken = input.accessToken.trim()
  if (!accessToken) {
    throw new ChatGeneratedApplicationDraftPreparationError(
      401,
      'chat-generated-application-draft-session-unavailable',
      false,
    )
  }
  if (
    !SAFE_ID.test(input.idempotencyKey) ||
    input.requestContent.trim().length < 1 ||
    input.requestContent.length > 32_768
  ) {
    throw new ChatGeneratedApplicationDraftPreparationError(
      400,
      'chat-generated-application-draft-request-invalid',
      false,
    )
  }

  let response: Response | null = null
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      response = await sendPreparationRequest({ ...input, accessToken })
      break
    } catch (error) {
      if (input.signal?.aborted) throw error
      if (attempt === 1) {
        throw new ChatGeneratedApplicationDraftPreparationError(
          0,
          'chat-generated-application-draft-unreachable',
          true,
        )
      }
    }
  }
  if (!response) {
    throw new ChatGeneratedApplicationDraftPreparationError(
      0,
      'chat-generated-application-draft-unreachable',
      true,
    )
  }
  const payload: unknown = await response.json().catch(() => null)
  if (!response.ok) throw responseError(response.status, payload)
  try {
    return parseChatGeneratedApplicationDraftPreparationReceipt(payload, input)
  } catch (error) {
    throw new ChatGeneratedApplicationDraftPreparationError(
      502,
      error instanceof Error
        ? error.message
        : 'chat-draft-preparation-response-invalid',
      false,
    )
  }
}

export function chatGeneratedApplicationDraftMarkerForReceipt(
  receipt: ChatGeneratedApplicationDraftPreparationReceipt,
): Record<string, unknown> {
  return {
    schemaVersion: 'cogo.platform-chat-generated-application-draft-pending.v1',
    status: 'pending-decompose',
    subject: structuredClone(receipt.subject),
    requestContentHash: receipt.requestContentHash,
    requestEnvelopeRef: structuredClone(receipt.requestEnvelopeRef),
    preparationReceiptRef: structuredClone(receipt.receiptRef),
  }
}

export function chatGeneratedApplicationDraftMarkerMatchesReceipt(
  settings: unknown,
  receipt: ChatGeneratedApplicationDraftPreparationReceipt,
): boolean {
  const marker = record(record(settings)?.chatGeneratedApplicationDraft)
  return Boolean(
    marker &&
      marker.schemaVersion ===
        'cogo.platform-chat-generated-application-draft-pending.v1' &&
      marker.status === 'pending-decompose' &&
      hashCanonicalContent(marker.subject) ===
        hashCanonicalContent(receipt.subject) &&
      marker.requestContentHash === receipt.requestContentHash &&
      hashCanonicalContent(marker.requestEnvelopeRef) ===
        hashCanonicalContent(receipt.requestEnvelopeRef) &&
      hashCanonicalContent(marker.preparationReceiptRef) ===
        hashCanonicalContent(receipt.receiptRef),
  )
}

export function assertChatGeneratedApplicationDraftProofForProjectSettings(
  settings: unknown,
  receipt: ChatGeneratedApplicationDraftPreparationReceipt | undefined,
): void {
  const marker = record(record(settings)?.chatGeneratedApplicationDraft)
  if (
    marker?.schemaVersion ===
      'cogo.platform-chat-generated-application-draft-pending.v1' &&
    !receipt
  ) {
    throw new Error(
      'App generation requires the exact Chat draft preparation proof for this reserved project.',
    )
  }
  if (receipt && !chatGeneratedApplicationDraftMarkerMatchesReceipt(settings, receipt)) {
    throw new Error(
      'App generation Chat draft preparation does not match the authenticated project.',
    )
  }
}

export interface ChatGeneratedApplicationDraftFinalizationReceipt {
  schemaVersion: typeof CHAT_GENERATED_APPLICATION_DRAFT_FINALIZATION_SCHEMA_VERSION
  status: 'finalized'
  idempotencyKey: string
  subject: GeneratedApplicationAuthoritySubject
  projectRevision: {
    authority: 'studio-project-server'
    value: string
  }
  settingsRevision: {
    authority: 'studio-project-settings-server'
    value: string
  }
  requestEnvelopeRef: ContentRef
  finalizedAt: string
  finalizationRef: ContentRef
}

export async function finalizeChatGeneratedApplicationDraft(input: {
  accessToken: string
  preparationReceipt: ChatGeneratedApplicationDraftPreparationReceipt
  name: string
  manifest: Record<string, unknown>
  settings: Record<string, unknown>
  signal?: AbortSignal
}): Promise<ChatGeneratedApplicationDraftFinalizationReceipt> {
  const response = await fetch(
    cogoPlatformApiUrl(
      `${CHAT_GENERATED_APPLICATION_DRAFT_PREPARATION_ROUTE}/${encodeURIComponent(input.preparationReceipt.subject.projectId)}/finalize`,
    ),
    {
      method: 'POST',
      headers: withCogoDevProxyHeaderRecord({
        Authorization: `Bearer ${input.accessToken}`,
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        idempotencyKey: input.preparationReceipt.idempotencyKey,
        preparationReceipt: input.preparationReceipt,
        expectedProjectRevision: input.preparationReceipt.projectRevision,
        expectedSettingsRevision: input.preparationReceipt.settingsRevision,
        name: input.name,
        manifest: input.manifest,
        settings: input.settings,
      }),
      signal: input.signal,
    },
  )
  const payload: unknown = await response.json().catch(() => null)
  if (!response.ok) throw responseError(response.status, payload)
  if (
    !exactKeys(payload, [
      'schemaVersion',
      'status',
      'idempotencyKey',
      'subject',
      'projectRevision',
      'settingsRevision',
      'requestEnvelopeRef',
      'finalizedAt',
      'finalizationRef',
    ])
  ) {
    throw new ChatGeneratedApplicationDraftPreparationError(
      502,
      'chat-draft-finalization-response-invalid',
      false,
    )
  }
  const receipt = payload as unknown as ChatGeneratedApplicationDraftFinalizationReceipt
  if (
    receipt.schemaVersion !==
      CHAT_GENERATED_APPLICATION_DRAFT_FINALIZATION_SCHEMA_VERSION ||
    receipt.status !== 'finalized' ||
    receipt.idempotencyKey !== input.preparationReceipt.idempotencyKey ||
    hashCanonicalContent(receipt.subject) !==
      hashCanonicalContent(input.preparationReceipt.subject) ||
    !exactKeys(receipt.projectRevision, ['authority', 'value']) ||
    receipt.projectRevision.authority !== 'studio-project-server' ||
    !exactKeys(receipt.settingsRevision, ['authority', 'value']) ||
    receipt.settingsRevision.authority !== 'studio-project-settings-server' ||
    !contentRef(receipt.requestEnvelopeRef) ||
    hashCanonicalContent(receipt.requestEnvelopeRef) !==
      hashCanonicalContent(input.preparationReceipt.requestEnvelopeRef) ||
    typeof receipt.finalizedAt !== 'string' ||
    Number.isNaN(Date.parse(receipt.finalizedAt)) ||
    !contentRef(receipt.finalizationRef)
  ) {
    throw new ChatGeneratedApplicationDraftPreparationError(
      502,
      'chat-draft-finalization-receipt-invalid',
      false,
    )
  }
  const { finalizationRef, ...body } = receipt
  if (finalizationRef.contentHash !== hashCanonicalContent(body)) {
    throw new ChatGeneratedApplicationDraftPreparationError(
      502,
      'chat-draft-finalization-receipt-self-hash-invalid',
      false,
    )
  }
  return structuredClone(receipt)
}
